import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from "rxjs/operators";
import {FileService} from './file.service';
import {CommonTransformationService} from './common-transformation.service';
import {BLOB_RESPONSE_TYPE} from '../classes/common';
import {DocFile, FilePermissionSetting} from '../classes/doc-file';
import {DocumentType} from '../enums/document-type.enum';
import {Transaction} from '../enums/txn-type.enum';
import {QueryResult} from "@app/shared";

@Injectable()
export class CommonDocRepoService {

  constructor(private http: HttpClient, private commonTransService: CommonTransformationService, private fileService: FileService) {
  }

  getDocTypeByName(docType: DocumentType): Observable<any> {
    const url = `/common/documents/type-names/${docType}`;

    return this.http.get(url);
  }

  getDocTypeById(docTypeId: number): Observable<any> {
    const url = `/common/documents/types/${docTypeId}`;

    return this.http.get(url);
  }

  getDocTypes(txnType?: Transaction): Observable<QueryResult> {
    let url = '/common/documents/types?start=0&count=-1';

    if (txnType) {
      url = url + `&transaction=${txnType}`;
    }

    return this.http.get<QueryResult>(url);
  }

  getDocTypeAsDropdown(): Observable<any> {

    return this.getDocTypes().pipe(map(res => {
      return this.commonTransService.convertToDropDown("name", res.list, null, true);
    }));
  }

  getAttributesForDocType(docType: DocumentType): Observable<any> {
    const url = `/common/documents/types/attributes?docTypeName=${docType}`;

    return this.http.get(url);
  }

  getFileListForTransaction(txnType: Transaction | number, refId: number): Observable<QueryResult> {
    let url = `/common/documents/folders/files?refId=${refId}&count=-1`;

    if (typeof txnType === "number") {
      url += `&transactionId=${txnType}`;
    } else {
      url += `&transaction=${txnType}`;
    }

    return this.http.get<QueryResult>(url);
  }

  getFileListForUserTransaction(txnType: Transaction | number, refId: number, userId: number): Observable<any> {
    let url = `/common/documents/folders/user-files?refId=${refId}&userId=${userId}&count=-1`;

    if (typeof txnType === "number") {
      url += `&transactionId=${txnType}`;
    } else {
      url += `&transaction=${txnType}`;
    }

    return this.http.get(url);
  }

  getFilesForTransaction(txnType: Transaction | number, refId: number): Observable<any> {
    let url = `/common/documents/folders/files?refId=${refId}&count=-1`;

    if (typeof txnType === "number") {
      url += `&transactionId=${txnType}`;
    } else {
      url += `&transaction=${txnType}`;
    }

    // need to specify a correct responseType otherwise, it default so to json
    // more info at https://angular.io/api/common/http/HttpClient#get
    return this.http.get(url, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});

  }

  saveFilesForTransaction(txnType: Transaction | number, refId: number, docFiles: any): Observable<any> {
    let url = `/common/documents/folders/files?refId=${refId}`;

    if (typeof txnType === "number") {
      url += `&transactionId=${txnType}`;
    } else {
      url += `&transaction=${txnType}`;
    }

    return this.http.post(url, docFiles);
  }

  getTemplateFiles(txnType: Transaction | number): Observable<QueryResult> {
    let url = `/common/documents/folders/templates?count=-1`;

    if (typeof txnType === "number") {
      url += `transactionId=${txnType}`;
    } else {
      url += `transaction=${txnType}`;
    }
    return this.http.get<QueryResult>(url);
  }

  saveTemplateFiles(txnType: Transaction | number, docFiles: any): Observable<any> {
    let url = `/common/documents/folders/templates?`;

    if (typeof txnType === "number") {
      url += `transactionId=${txnType}`;
    } else {
      url += `transaction=${txnType}`;
    }
    return this.http.post(url, docFiles);
  }

  getFileById(fileId: number): Observable<any> {
    const url = `/common/documents/files/${fileId}`;

    //SSO-7039, To display the image in new tab, use responseType 'blob' as 'json'
    return this.http.get<any>(url, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});

  }

  getFileData(docFile: DocFile): Observable<any> {
    const url = `/common/documents/files/${docFile?.id}/data`;
    return this.http.post<any>(url,docFile, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});
  }

  deleteFileById(fileId: number): Observable<any> {
    const url = `/common/documents/files/${fileId}`;

    return this.http.delete(url);
  }

  getFileByName(txnType: Transaction | number, refId: number, fileName: string): Observable<any> {
    let url = `/common/documents/files?refId=${refId}&fileName=${fileName}&`;

    if (typeof txnType === "number") {
      url += `transactionId=${txnType}`;
    } else {
      url += `transaction=${txnType}`;
    }

    return this.http.get(url, {observe: 'response', responseType: BLOB_RESPONSE_TYPE});

  }

  deleteFileByName(txnType: Transaction | number, refId: number, fileName: string): Observable<any> {
    let url = `/common/documents/files?refId=${refId}&fileName=${fileName}&`;

    if (typeof txnType === "number") {
      url += `transactionId=${txnType}`;
    } else {
      url += `transaction=${txnType}`;
    }

    return this.http.delete(url);
  }

  getFileByIdThenShowOnNewTab(fileId: number) {

    this.getFileById(fileId).subscribe(data => {
      this.fileService.showFileOnNewTab(data);
    });
  }

  getFileByIdThenSave(fileId: number) {

    this.getFileById(fileId).subscribe(data => {
      this.fileService.saveFileToLocalStorage(data.fileName, data);
    });
  }

  getFileDataThenSave(docFile: DocFile) {
    this.getFileData(docFile).subscribe(data => {
      this.fileService.saveFileToLocalStorage(data.fileName, data);
    });
  }

  getFileByNameThenShowOnNewTab(txnType: Transaction | number, refId: number, fileName: string) {

    this.getFileByName(txnType, refId, fileName).subscribe(data => {
      this.fileService.showFileOnNewTab(data);
    });
  }

  getFileByNameThenSave(txnType: Transaction | number, refId: number, fileName: string) {

    this.getFileByName(txnType, refId, fileName).subscribe(data => {
      this.fileService.saveFileToLocalStorage(data.fileName, data);
    });
  }

  getFilePermissions(fileId: number): Observable<QueryResult> {
    const url = `/common/documents/files/${fileId}/settings`;
    return this.http.get<QueryResult>(url);
  }

  updateFilePermissions(fileId: number, permissions: FilePermissionSetting[]): Observable<QueryResult> {
    const url = `/common/documents/files/${fileId}/settings`;
    return this.http.put<QueryResult>(url, permissions);
  }

  loadDocData(file: DocFile, selectedFile) {
    const reader = new FileReader();

    if (selectedFile) {
      reader.readAsBinaryString(selectedFile);
    }
    reader.onloadend = (e) => {
      const target: any = e.target;
      const binaryStr = target.result;

      // convert into byte[]
      let byteArr = [];
      for (let i = 0; i < binaryStr.length; i++) {
        byteArr.push(binaryStr.charCodeAt(i));
      }

      file.data = byteArr;
    };
  }

  loadFileData(selectedFile): Observable<any[]> {
    const reader = new FileReader();

    if (selectedFile) {
      reader.readAsBinaryString(selectedFile);
    }
    return Observable.create(observer => {
      reader.onloadend = (e) => {
        let target: any = e.target;
        let binaryStr = target.result;

        // convert into byte[]
        let byteArr = [];
        for (let i = 0; i < binaryStr.length; i++) {
          byteArr.push(binaryStr.charCodeAt(i));
        }

        observer.next(byteArr);
        observer.complete();
      };
    });
  }
}
